import React, { useMemo, useState } from 'react';
import Icon from '@mdi/react';
import {
    mdiAlertCircleOutline,
    mdiAlertOutline,
    mdiCheckCircle,
    mdiKeyVariant,
    mdiPlaylistCheck,
    mdiTrashCanOutline,
} from '@mdi/js';
import Botao from '@/components/Botao';
import AlertBox from '@/components/layout/AlertBox';
import styles from './PlanoRevisao.module.css';
import type { IaOperacaoDTO } from '@webip/shared/src/dto/ia';

/**
 * `@webip/shared` só entra aqui como `import type` — o alias do Vite não resolve
 * import de runtime desse pacote. Daí a checagem por prefixo em vez de um helper
 * compartilhado: toda ferramenta destrutiva se chama `remover_*`, garantido pelo
 * tipo `IaFerramentaRemocao` no backend.
 */
const ehRemocao = (ferramenta: string) => ferramenta.startsWith('remover_');

/**
 * Revisão do plano montado pela IA — o gate humano antes de qualquer gravação.
 *
 * Os campos secretos chegam aqui com o marcador `<pendente>`: o valor real é
 * digitado neste formulário e enviado direto ao endpoint de aplicar, sem nunca
 * passar pelo modelo nem pelo histórico da conversa.
 */

type Props = {
    operacoes: IaOperacaoDTO[];
    aplicando: boolean;
    somenteLeitura: boolean;
    onAplicar: (segredos: Record<string, Record<string, string>>) => void;
    onDescartar: () => void;
};

// Ids e estruturas são para o replay; o operador lê a versão legível que vem
// ao lado (`dispositivos`, `grupos`, `escopo`, `lista_regras`, `antes`/`depois`).
const CAMPOS_OCULTOS = new Set([
    'appliance_id',
    'tipo',
    'id',
    'dispositivo_ids',
    'grupo_ids',
    'rede_ids',
    'scope',
    'politica_id',
    'regras',
    'regra',
    'regra_ids',
    'corpo', // política de entrada: corpo da rota, legível em escopo/prioridade/lista_regras
]);

const OperacaoCampos = ({ payload }: { payload: Record<string, unknown> }) => {
    const entradas = Object.entries(payload).filter(
        ([chave, valor]) =>
            !CAMPOS_OCULTOS.has(chave) && valor != null && valor !== '' &&
            !(Array.isArray(valor) && valor.length === 0),
    );

    if (!entradas.length) return null;

    return (
        <dl className={styles.campos}>
            {entradas.map(([chave, valor]) => (
                <React.Fragment key={chave}>
                    <dt>{chave}</dt>
                    <dd>{typeof valor === 'object' ? JSON.stringify(valor) : String(valor)}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
};

const PlanoRevisao = ({ operacoes, aplicando, somenteLeitura, onAplicar, onDescartar }: Props) => {
    const [segredos, setSegredos] = useState<Record<string, Record<string, string>>>({});

    const pendentes = useMemo(
        () => operacoes.filter((o) => o.status === 'pendente'),
        [operacoes],
    );

    const camposSecretos = useMemo(
        () =>
            pendentes.flatMap((o) =>
                (o.segredos_pendentes ?? []).map((s) => ({ operacaoId: String(o.id), ...s })),
            ),
        [pendentes],
    );

    const remocoesPendentes = useMemo(
        () => pendentes.filter((o) => ehRemocao(o.ferramenta)),
        [pendentes],
    );

    const faltaSegredo = camposSecretos.some(
        ({ operacaoId, campo }) => !segredos[operacaoId]?.[campo]?.trim(),
    );

    if (!operacoes.length) return null;

    const definirSegredo = (operacaoId: string, campo: string, valor: string) => {
        setSegredos((atual) => ({
            ...atual,
            [operacaoId]: { ...(atual[operacaoId] ?? {}), [campo]: valor },
        }));
    };

    return (
        <div className={styles.container}>
            <div className={styles.cabecalho}>
                <Icon path={mdiPlaylistCheck} size={0.9} />
                <span>
                    Plano — {operacoes.length} {operacoes.length === 1 ? 'operação' : 'operações'}
                </span>
            </div>

            <ol className={styles.lista}>
                {operacoes.map((operacao) => (
                    <li
                        key={operacao.id}
                        className={[
                            styles[`op_${operacao.status}`],
                            // Remoção pendente fica marcada: aprovar por engano
                            // custa bem mais caro que aprovar uma criação.
                            ehRemocao(operacao.ferramenta) && operacao.status === 'pendente'
                                ? styles.opRemocao
                                : '',
                        ].join(' ')}
                    >
                        <div className={styles.resumo}>
                            {operacao.status === 'aplicada' && (
                                <Icon path={mdiCheckCircle} size={0.7} className={styles.iconeOk} />
                            )}
                            {operacao.status === 'falhou' && (
                                <Icon
                                    path={mdiAlertCircleOutline}
                                    size={0.7}
                                    className={styles.iconeErro}
                                />
                            )}
                            {ehRemocao(operacao.ferramenta) && operacao.status === 'pendente' && (
                                <Icon
                                    path={mdiTrashCanOutline}
                                    size={0.7}
                                    className={styles.iconeRemocao}
                                />
                            )}
                            <span>{operacao.resumo}</span>
                        </div>
                        {operacao.alerta && operacao.status === 'pendente' && (
                            <div className={styles.alerta}>
                                <Icon path={mdiAlertOutline} size={0.7} />
                                <span>{operacao.alerta}</span>
                            </div>
                        )}
                        <OperacaoCampos payload={operacao.payload} />
                        {operacao.erro && <div className={styles.erro}>{operacao.erro}</div>}
                    </li>
                ))}
            </ol>

            {!somenteLeitura && camposSecretos.length > 0 && (
                <div className={styles.segredos}>
                    <div className={styles.segredosTitulo}>
                        <Icon path={mdiKeyVariant} size={0.75} />
                        <span>Dados sensíveis</span>
                    </div>
                    <p className={styles.segredosAjuda}>
                        Estes valores não passam pela IA — são enviados direto ao servidor ao aplicar.
                    </p>
                    {camposSecretos.map(({ operacaoId, campo, rotulo }) => (
                        <label key={`${operacaoId}.${campo}`} className={styles.campoSegredo}>
                            <span>{rotulo}</span>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={segredos[operacaoId]?.[campo] ?? ''}
                                onChange={(e) => definirSegredo(operacaoId, campo, e.target.value)}
                            />
                        </label>
                    ))}
                </div>
            )}

            {!somenteLeitura && pendentes.length > 0 && (
                <>
                    {remocoesPendentes.length > 0 ? (
                        <AlertBox variant="warning" style={{ margin: '0 0 8px' }}>
                            Este plano remove {remocoesPendentes.length}{' '}
                            {remocoesPendentes.length === 1 ? 'item' : 'itens'}, incluindo os que
                            ficariam órfãos. Confira a lista acima — cada linha é uma exclusão.
                        </AlertBox>
                    ) : (
                        <AlertBox variant="info" style={{ margin: '0 0 8px' }}>
                            Nada foi alterado ainda. Ao aplicar, as mudanças entram no cadastro do
                            WebIP; o equipamento só muda no sincronismo.
                        </AlertBox>
                    )}
                    <div className={styles.acoes}>
                        <Botao
                            variant="outline-secondary"
                            size="sm"
                            disabled={aplicando}
                            onClick={onDescartar}
                        >
                            Descartar
                        </Botao>
                        <Botao
                            variant={remocoesPendentes.length > 0 ? 'danger' : 'primary'}
                            size="sm"
                            disabled={aplicando || faltaSegredo}
                            onClick={() => onAplicar(segredos)}
                        >
                            {aplicando
                                ? 'Aplicando…'
                                : remocoesPendentes.length > 0
                                    ? 'Aplicar (inclui remoções)'
                                    : 'Aplicar plano'}
                        </Botao>
                    </div>
                </>
            )}
        </div>
    );
};

export default PlanoRevisao;
